import React, { useCallback, useEffect, useRef, useState } from "react";

/**
 * Hook that keeps a MUI `TextField` label shrunk when the browser autofills
 * the underlying `<input>` and optionally syncs the autofilled value back
 * into the consumer's controlled React state.
 *
 * Why this exists:
 * - Browser / password-manager autofill can populate an `<input>` without
 *   firing a React `onChange`. With a controlled MUI TextField, that leaves
 *   the label overlapping the value and, worse, leaves React state empty
 *   (which breaks button-enable logic, validation, and submit payloads).
 * - MUI dispatches internal CSS animations (`mui-auto-fill` /
 *   `mui-auto-fill-cancel`) on the input whenever autofill enters or
 *   leaves the field. We listen for those animations to detect autofill
 *   in a DOM-only reliable way.
 *
 * @param value - The current controlled value of the input. Used to derive
 *   `shouldShrink` so the label stays shrunk while the user has typed
 *   something OR while autofill is active.
 * @param onAutofillValue - Optional callback invoked when autofill fires
 *   with a non-empty value. Consumers use this to push the autofilled
 *   value into their controlled state (applying any normalization such as
 *   whitespace stripping). If omitted, the hook only handles label shrink.
 *
 * @returns Object with:
 *   - `shouldShrink`: `true` when the label must stay shrunk (value present
 *     or autofill active), otherwise `undefined` so MUI falls back to its
 *     default float behavior.
 *   - `autofillProps`: Props to spread onto the TextField's `htmlInput` slot
 *     (currently just `onAnimationStart`). The handler reference is stable
 *     across renders so it never re-attaches.
 *
 * @example
 * const [email, setEmail] = useState("");
 * const { shouldShrink, autofillProps } = useAutofillShrink(email, setEmail);
 *
 * return (
 *   <TextField
 *     label="Email"
 *     value={email}
 *     onChange={(e) => setEmail(e.target.value)}
 *     slotProps={{
 *       htmlInput: autofillProps,
 *       inputLabel: { shrink: shouldShrink },
 *     }}
 *   />
 * );
 */
export const useAutofillShrink = (
  value: string,
  onAutofillValue?: (autofilledValue: string) => void,
) => {
  const [isAutofilled, setIsAutofilled] = useState(false);
  const onAutofillValueRef = useRef(onAutofillValue);

  useEffect(() => {
    onAutofillValueRef.current = onAutofillValue;
  }, [onAutofillValue]);

  const onAnimationStart = useCallback(
    (event: React.AnimationEvent<HTMLInputElement>) => {
      if (event.animationName === "mui-auto-fill") {
        setIsAutofilled(true);
        const autofilledValue = event.currentTarget?.value ?? "";
        if (autofilledValue && onAutofillValueRef.current) {
          onAutofillValueRef.current(autofilledValue);
        }
      } else if (event.animationName === "mui-auto-fill-cancel") {
        setIsAutofilled(false);
      }
    },
    [],
  );

  const shouldShrink: true | undefined =
    Boolean(value) || isAutofilled ? true : undefined;

  return {
    shouldShrink,
    autofillProps: { onAnimationStart },
  };
};
